// ----------------------------------------------------------------------
// Dashboard i18n — staff-facing strings (manager / chef / waiter UIs).
//
// Scope (V1): the strings staff actually SEE and TAP during a normal
// shift — navbar labels, order status badges, action buttons, and the
// most-used contextual nouns (Table, Round, Items, Total).
//
// Translation status (2026-06-05):
//   EN  — canonical. Nav labels renamed to the new IA (Service
//         Management / Menu Management / Table & QR Setup / Team
//         Management / Manager Insights).
//   AM  — REVIEWED (May–Jun 2026 review sheet) for original strings;
//         values for RENAMED/NEW nav labels are DRAFTS pending review.
//   OM  — REVIEWED (review-sheet corrections applied 2026-06-05);
//         RENAMED/NEW nav labels are DRAFTS pending review.
//   TI  — MACHINE DRAFT (2026-06-05), full set. Native review pending.
//   SID — MACHINE DRAFT (2026-06-05), low confidence, loanword-heavy.
//         MUST be verified by a native Sidaamu Afoo speaker before the
//         Hawassa pilot.
//   NL  — drafted 2026-06-05 (high confidence; verify with NL staff
//         when Eindhoven onboards).
//
// Lookup priority: STRINGS[lang][key] → STRINGS.en[key] → key.
// Review sheet: docs/i18n/ODRA_Translation_Review.xlsx
// ----------------------------------------------------------------------

export const SUPPORTED_DASHBOARD_LANGS = ["en", "am", "om", "sid", "ti", "nl"];

// Short display labels for the language picker. Native script where
// available so a non-English-reading waiter recognises their language.
export const LANG_DISPLAY = {
  en:  "EN",
  am:  "አማ",
  om:  "AO",
  sid: "A-Sid",
  ti: "ትግ",
  nl:  "NL",
};

export const STRINGS = {
  // ===================================================================
  // ENGLISH — canonical source.
  // ===================================================================
  en: {
    // ---- Navbar links (order matches App.js navbar order) ----
    nav_order_queue:      "Order Status",
    nav_take_order:       "Captain Orders",
    nav_end_of_day:       "End of Day (F&B summary)",
    nav_tabs:             "Open Orders",
    nav_home:             "Home",
    nav_reports:          "Reports",
    nav_adjustments:      "Adjustments",
    nav_menu:             "Menu Items",
    nav_recommended:      "Recommendations",
    nav_tables:           "Tables & Rooms",
    nav_qr_codes:         "QR Codes",
    nav_staff:            "Staff Accounts",
    nav_assignments:      "Waiter Assignment",
    nav_ratings:          "Ratings & Feedback",
    nav_admin:            "Admin",
    nav_logout:           "Logout",
    // Waiter-side widget on the Order Status page. Shows which tables
    // this waiter is assigned to today.
    my_tables_today:      "Your tables today",
    no_tables_assigned:   "No tables assigned today",
    ask_manager:          "Ask the manager to assign you tables.",
    // Top-level dropdown groups in the navbar.
    nav_group_frontdesk:  "Front Desk",
    nav_reservations:     "Reservations",
    nav_groups:           "Group bookings",
    nav_rooms:            "Rooms",
    nav_housekeeping:     "Housekeeping",
    nav_guest_list:       "Guest list",
    nav_lost_found:       "Lost & Found",
    nav_maintenance:      "Maintenance",
    nav_rooms_report:     "Rooms report",
    nav_room_types:       "Room types",
    nav_rate_plans:       "Rate plans",
    nav_board:            "Board",
    nav_companies:        "Companies",
    nav_proformas:        "Proformas",
    nav_support:          "Help & Support",
    nav_security:         "Security",
    nav_privacy:          "Data protection",
    nav_hotel_settings:   "Hotel settings",
    nav_activity:         "Activity log",
    nav_partner_hotels:   "Partner hotels",
    nav_outsourced:       "Outsourced rooms",
    nav_night_audit:      "Night audit",
    nav_finance_reports:  "Finance reports",
    nav_my_till:          "My till",
    nav_tills:            "Tills",
    nav_salesperson_report: "Salesperson sales",
    nav_forex:            "Forex report",
    nav_events:           "Events & Functions",
    nav_handover:         "Shift handover",
    svc_grp_orders:       "Ordering",
    svc_grp_menu:         "Menu",
    svc_grp_tables:       "Tables",
    svc_grp_cashier:      "Cashier & tabs",
    fd_grp_ops:           "Operations",
    fd_grp_reception:     "Reception & bookings",
    fd_grp_housekeeping:  "Housekeeping & upkeep",
    fd_grp_dayclose:      "Day close",
    fd_grp_setup:         "Setup",
    fd_grp_analytics:     "Analytics",
    nav_reports:          "Reports",
    nav_group_orders:     "Service Management",
    nav_group_menu:       "Menu Management",
    nav_group_tableqr:    "Table & QR Setup",
    nav_group_team:       "Team Management",
    nav_group_insights:   "Manager Insights",
    // Department-grouped nav (sidebar switcher + home launcher).
    nav_departments:      "Departments",
    dept_frontdesk:       "Front desk",
    dept_service:         "Restaurant service",
    dept_services:        "Leisure & Wellness",
    nav_services_catalog: "Services",
    nav_members:          "Members",
    nav_checkin:          "Check-in",
    nav_renewals:         "Renewals",
    nav_classes:          "Classes",
    nav_packages:         "Packages",
    nav_membership_plans: "Service Bundles",
    nav_service_bookings: "Bookings",
    nav_service_schedule: "Schedule",
    nav_resources:        "Resources",
    dept_kitchen:         "Kitchen",
    dept_team:            "Team",
    dept_insights:        "Insights",
    dept_reports:         "Reports",
    rpt_grp_close:        "Financial close",
    rpt_grp_analytics:    "Business analytics",
    nav_fnb_analytics:    "F&B analytics",
    nav_business_overview: "Business overview",
    // Daily routines (Morning open / End of day).
    rt_routines:          "Daily routines",
    rt_morning_title:     "Morning open",
    rt_morning_sub:       "Get the hotel ready for the day",
    rt_evening_title:     "End of day",
    rt_evening_sub:       "Close the day cleanly across departments",
    rt_progress:          "{n} of {m} done",
    rt_all_done:          "Routine complete",
    rt_all_done_sub:      "Every step is handled. Nice work.",
    rt_open:              "Open",
    rt_left:              "{n} left",
    rt_back:              "Back to home",
    m_rooms:              "Rooms ready",
    m_rooms_d:            "Confirm cleaned rooms are inspected and ready",
    m_arrivals:          "Arrivals & departures",
    m_arrivals_d:        "Review who checks in and out today",
    m_stock:             "Stock for the day",
    m_stock_d:           "Check low-stock items before service",
    m_menu:              "Menu availability",
    m_menu_d:            "Set today's specials and out-of-stock items",
    m_staff:             "Staff on shift",
    m_staff_d:           "Confirm assignments for today",
    e_tabs:              "Close open tabs",
    e_tabs_d:            "Settle any tabs still open from today",
    e_usage:             "Confirm usage",
    e_usage_d:           "Approve pending kitchen usage logs",
    e_review:            "Review discounts & voids",
    e_review_d:          "Check today's discounts and cancellations",
    e_guests:            "Guest list for police",
    e_guests_d:          "Print tonight's in-house guest report",
    e_report:            "End-of-day report",
    e_report_d:          "Run and file today's sales summary",

    // ---- Order status labels (used on kitchen queue + open orders) ----
    status_pending:       "Pending",
    status_preparing:     "Preparing",
    status_ready:         "Ready",
    status_served:        "Served",
    status_cancelled:     "Cancelled",
    status_open:          "Open",
    status_closed:        "Closed",

    // Kitchen-queue tab filters (dashboard-specific, not real statuses)
    tab_active:           "Active",
    tab_scheduled:        "Scheduled", serve_at: "Serve at", fire_now: "Fire now",
    tab_all_today:        "All today",
    refresh:              "Refresh",
    loading:              "Loading…",
    no_orders_here:       "No orders here.",
    tab_ready_to_run:     "🛎️ Ready to run",
    no_ready_items:       "Nothing ready to run yet.",
    signed_in_as:         "Signed in as",
    live:                 "live",
    polling:              "polling",

    // ---- Action buttons ----
    btn_add:              "Add",
    btn_remove:           "Remove",
    btn_submit:           "Submit",
    btn_save:             "Save",
    btn_cancel:           "Cancel",
    btn_confirm:          "Confirm",
    btn_edit:             "Edit",
    btn_delete:           "Delete",
    btn_back:             "Back",
    btn_mark_preparing:   "Start preparing",
    btn_mark_ready:       "Mark ready",
    btn_mark_served:      "Mark served",
    btn_close_tab:        "Close order",
    btn_submit_order:     "Submit order",
    btn_place_order:      "Place order",
    btn_mark_completed:   "Mark completed",
    receipt:              "Receipt",
    just_now:             "just now",

    // ---- Common contextual nouns ----
    table:                "Table",
    round:                "Round",
    order:                "Order",
    items:                "Items",
    item:                 "Item",
    total:                "Total",
    notes:                "Notes",
    note_for_kitchen:     "Note for kitchen",
    waiter:               "Waiter",
    chef:                 "Chef",
    manager:              "Manager",
    customer:             "Customer",
    guest:                "Guest",

    // ---- Captain Order page ----
    take_order_title:     "Captain order \u2014 take order at table",
    select_table:         "Select table",
    search_items:         "Search menu items",
    cart:                 "Cart",
    cart_empty:           "Cart is empty — tap an item above to add it.",
    add_item_to_order:    "Add to order",
    review_and_submit:    "Review &amp; submit",

    // ---- Kitchen / Order Status page ----
    kitchen_queue_title:  "Kitchen queue",
    no_orders_yet:        "No orders yet.",
    time_ago_minutes:     "min ago",
    time_ago_just_now:    "just now",

    // ---- Open Orders page ----
    tabs_title:           "Open orders",
    no_open_tabs:         "No open orders right now.",
    new_round:            "New round",
    running_total:        "Running total",

    // ---- F&B Cost Control (cost_control feature; Business / Enterprise) ----
    cc_section:           "F&B Cost Control",
    cc_nav_ingredients:   "Ingredients",
    cc_nav_recipes:       "Recipes",
    cc_nav_usage:         "Record usage",
    cc_nav_variance:      "Efficiency Variance",
    menueng_title:        "Menu engineering",
    fr_title:             "Food recovery",
    menueng_subtitle:     "which dishes make money",
    menueng_intro:        "Every dish placed by profit (price − recipe cost) and popularity. Stars earn and sell; plowhorses sell but earn little; puzzles earn but don't sell; dogs do neither.",
    menueng_avg_margin:   "Menu average margin",
    menueng_pop_line:     "popularity line",
    menueng_units:        "units",
    menueng_dishes:       "dishes",
    menueng_uncosted:     "Sold dishes with no recipe (can't be costed, excluded)",
    menueng_no_rows:      "No sales in this range.",
    menueng_what_to_do:   "What to do",
    menueng_dish:         "Dish",
    menueng_sold:         "Sold",
    menueng_price:        "Price",
    menueng_cost:         "Recipe cost",
    menueng_margin:       "Margin",
    menueng_fcpct:        "Food %",
    menueng_contribution: "Contribution",
    menueng_class:        "Class",
    cc_log_waste:         "Log waste / spoilage",
    cc_qty:               "Qty",
    cc_reason:            "Reason",
    cc_log:               "Log",
    cc_saved:             "Saved",
    cc_waste_fill:        "Pick an ingredient and quantity.",
    cc_waste:             "Waste",
    cc_waste_spoilage:    "Spoilage",
    cc_waste_over_prep:   "Over-prep",
    cc_waste_trim:        "Trim",
    cc_waste_breakage:    "Breakage",
    cc_waste_staff_meal:  "Staff meal",
    cc_waste_comp:        "Comp",
    cc_waste_other:       "Other",
    cc_dq_title:          "Data quality — numbers may be understated",
    cc_dq_missing_recipes: "Sold dishes with no recipe (counted as zero cost)",
    cc_dq_zero_cost:      "Recipe ingredients with zero cost",
    cc_trend_title:       "Food cost trend — last 14 days",
    cc_stock:             "Stock",
    cc_reorder_at:        "Reorder at",
    cc_par:               "Par",
    cc_supplier:          "Supplier",
    dept_restaurant:      "Restaurant",
    dept_wellness:        "Gym, Spa & Wellness",
    wel_grp_desk:         "Front desk",
    wel_grp_members:      "Membership",
    wel_grp_programs:     "Classes & services",
    dept_inventory:       "F&B Cost Control",
    rst_grp_service:      "Service",
    rst_grp_kitchen:      "Kitchen",
    inv_grp_setup:        "Setup",
    inv_grp_stock:        "Purchasing & stock",
    inv_grp_analysis:     "Analysis",
    nav_cashier:          "Cashier",
    nav_my_code:          "My code",
    nav_run:              "Ready to run",
    nav_purchasing:       "Purchasing",
    bo_nav:               "Back Office",
    dept_backoffice:      "Back office",
    bo_grp_procurement:   "Procurement",
    bo_grp_stores:        "Stores & stock",
    bo_grp_catalogue:     "Catalogue",
    bo_requisitions:      "Requisitions",
    bo_receiving:         "Receiving",
    bo_stock:             "Stock on hand",
    bo_transfers:         "Transfers",
    bo_issues:            "Issues",
    bo_stores:            "Stores",
    bo_items:             "Items",
    bo_settings:          "Approval policy",
    nav_stocktake:        "Stocktake",
    nav_schedule:         "Schedule",
    cc_name:              "Name",
    cc_unit:              "Unit",
    cc_category:          "Category",
    cc_optional:          "optional",
    cc_cost_per_unit:     "Cost / unit",
    cc_saving:            "Saving…",
    cc_could_not_load:    "Could not load.",
    cc_could_not_save:    "Could not save.",
    cc_from:              "From",
    cc_to:                "To",
    cc_today:             "Today",
    cc_yesterday:         "Yesterday",
    cc_last_7_days:       "Last 7 days",
    // Ingredients page
    cc_ing_intro:         "Your pantry and unit costs. Tip: enter the cost per single unit (e.g. for a 50 kg sack at 30,000, the cost per kg is 600).",
    cc_ing_name_ph:       "e.g. Chicken",
    cc_name_unit_required:"Name and unit are required.",
    cc_could_not_add:     "Could not add.",
    cc_adding:            "Adding…",
    cc_ingredient:        "Ingredient",
    cc_no_ingredients:    "No ingredients yet — add your first above.",
    cc_delete_confirm:    "Delete {name}? Its recipe lines will be removed too.",
    // Recipes page
    cc_rec_subtitle:      "standard portions",
    cc_rec_need_ings:     "Add ingredients first on the Ingredients page, then build recipes here.",
    cc_dish:              "Dish",
    cc_no_lines:          "No ingredients in this recipe yet.",
    cc_add_ingredient:    "+ Add ingredient",
    cc_dish_cost:         "Dish cost",
    cc_menu_price:        "Menu price",
    cc_food_cost_pct:     "Food cost %",
    cc_suggested_price:   "Suggested price",
    cc_margin:            "Margin",
    cc_food_target:       "Food target %",
    cc_drinks_target:     "Drinks target %",
    cc_price_ok:          "Priced well",
    cc_price_watch:       "A little low",
    cc_price_high:        "Underpriced",
    cc_targets_hint:      "Target food-cost %. Suggested price = dish cost ÷ target, rounded up.",
    cc_save_recipe:       "Save recipe",
    cc_saved_std_cost:    "Saved standard cost:",
    cc_could_not_load_recipe: "Could not load recipe.",
    // Record usage page
    cc_usage_intro:       "Enter how much of each ingredient was actually used and how much was returned to the store. The controller reviews and confirms.",
    cc_day:               "Day",
    cc_no_ings_defined:   "No ingredients defined yet.",
    cc_save_usage:        "Save usage",
    cc_saved_usage_for:   "Saved usage for {date}.",
    // Variance page
    cc_var_subtitle:      "standard vs actual usage",
    cc_var_intro:         "Standard usage (recipes × completed orders) vs the actual usage your chef recorded. The gap is the Efficiency Variance — over-portioning, spoilage or loss. Leftover returned to the store is tracked separately, and anything issued but neither used nor returned shows as unaccounted loss.",
    cc_revenue:           "Sales Revenue",
    cc_orders:            "Completed Orders",
    cc_theo_food:         "Standard Usage",
    cc_actual_food:       "Actual Usage",
    cc_gross_profit:      "Gross Profit",
    cc_gross_profit_note: "Revenue minus food cost — before labour, rent & overhead.",
    cc_nav_issue:           "Ingredient Issue Record",
    cc_issue_intro_ctrl:    "Register the ingredients you issue to the kitchen this morning. The chef confirms what was received.",
    cc_issue_intro_chef:    "Confirm the ingredients received from the store today. Adjust any quantity that differs from what was issued.",
    cc_issued:              "Kitchen Issue",
    cc_received:            "Received",
    cc_issued_food:         "Kitchen Issue",
    cc_issue_save:          "Save issue",
    cc_issue_confirm:       "Confirm receipt",
    cc_issue_confirm_hint:  "Quantities default to what was issued — change only the ones that differ.",
    cc_issue_status_none:   "Not registered yet",
    cc_issue_status_issued: "Issued — awaiting kitchen confirmation",
    cc_issue_status_confirmed: "Confirmed by kitchen",
    cc_issue_none_for_day:  "No issue registered for this day yet.",
    cc_issue_saved:         "Issue saved",
    cc_issue_confirmed_msg: "Receipt confirmed",
    cc_issue_mismatch:      "differs from issued",
    go_nav: "Group orders (F&B)",
    go_intro: "Multi-person restaurant orders booked by phone or in person — no hall; billed as one, with a kitchen prep & purchase list.",
    go_new: "New group order",
    go_back: "Back to list",
    go_name: "Order name",
    go_name_ph: "e.g. Awash Bank meeting",
    go_name_required: "An order name is required.",
    go_organizer: "Company / person",
    go_organizer_ph: "Who the order is for",
    go_contact: "Contact person",
    go_phone: "Phone",
    go_when: "Date & time",
    go_headcount: "Headcount",
    go_status: "Status",
    go_note: "Note",
    go_dishes: "Dishes",
    go_add_dish: "Add dish",
    go_no_dishes: "No dishes added yet.",
    go_remove: "Remove",
    go_total: "Order total",
    go_prep_title: "Prep & purchase list",
    go_prep_hint: "Add recipes to these dishes (in F&B Cost Control) to see the ingredient quantities to prepare and buy.",
    go_prep_save_first: "Save the order to see the prep & purchase list.",
    go_ingredient: "Ingredient",
    go_qty_needed: "Qty needed",
    go_est_cost: "Est. cost",
    go_prep_total: "Estimated ingredient cost",
    go_print: "Print purchase list",
    go_purchase_list_title: "Purchase / Prep List",
    go_when: "When",
    go_days: "Days",
    go_organizer: "Organizer",
    go_print_footer: "Generated by ODRA",
    go_popup_blocked: "Please allow pop-ups to print this list.",
    go_save: "Save",
    go_saving: "Saving…",
    go_saved: "Saved",
    go_delete: "Delete",
    go_cancel: "Cancel",
    go_confirm_delete: "Delete this group order?",
    go_could_not_save: "Could not save.",
    go_could_not_load: "Could not load.",
    go_none: "No group orders yet.",
    go_no_date: "No date set",
    go_pax: "pax",
    go_status_draft: "Draft",
    go_status_confirmed: "Confirmed",
    go_status_in_prep: "In prep",
    go_status_served: "Served",
    go_status_closed: "Closed",
    go_status_cancelled: "Cancelled",
    go_assign_staff: "Assigned staff (chefs & waiters)",
    go_no_staff: "Add chefs or waiters on the Staff page first.",
    go_none_assigned: "No group orders assigned to you yet.",
    go_to_prepare: "To prepare",
    go_start_prep: "Start prep",
    go_mark_served: "Mark served",
    go_refresh: "Refresh",
    go_days: "Days",
    go_add_buffet: "Add buffet",
    go_buffet: "Buffet",
    buf_nav: "Buffet Packages",
    buf_intro: "Buffet packages for group orders — set a per-head price and what's included. Hidden from guests and Captain Order.",
    buf_new: "New buffet",
    buf_name: "Name",
    buf_level: "Level",
    buf_price: "Per-head price",
    buf_def: "What's included",
    buf_def_ph: "e.g. Mains: Doro Wat, Kitfo · Hot beverages · Dessert",
    buf_active: "Active",
    buf_save: "Save",
    buf_saved: "Saved",
    buf_none: "No buffet packages yet.",
    buf_confirm_delete: "Delete this buffet?",
    cc_theo_food_pct:     "Standard food cost %",
    cc_actual_food_pct:   "Actual food cost %",
    cc_variance_cost:     "Efficiency Variance (cost)",
    cc_no_actual_notice:  "No actual usage recorded for this window yet — ask the chef to enter it on Record usage. Standard figures show regardless.",
    cc_should_theory:     "Standard Usage",
    cc_actual:            "Actual Usage",
    cc_returned:          "Returned",
    cc_loss:              "Unaccounted loss",
    cc_confirm:           "Confirm",
    cc_confirmed:         "Confirmed",
    cc_pending:           "Pending",
    cc_confirm_usage:     "Confirm day",
    cc_shift:             "Shift",
    cc_received:          "Received",
    cc_used:              "Used",
    cc_handover:          "Handover",
    cc_accept_handover:   "Accept handover",
    cc_shift_hint:        "Shift 1 opens with the morning issue; each later shift opens with the previous shift's handover. The last shift's handover is the return to the store.",
    cc_return_to_store:   "Return to store",
    cc_closing_shift:     "Closing shift (return to store)",
    cc_overview:          "Overview",
    cc_home_sub:          "Your food-cost cockpit — today at a glance.",
    cc_pending_confirm:   "Today's usage is awaiting your confirmation",
    cc_quick_actions:     "Quick actions",
    cc_cost_impact:       "Cost impact",
    cc_var_no_rows:       "No ingredient usage in this window (no recipes on sold dishes yet?).",
  },

  // ===================================================================
  // AMHARIC (am) — REVIEWED values from the review sheet. Items marked
  // "DRAFT" are new/renamed labels (2026-06-05) pending expert review.
  // ===================================================================
  am: {
    nav_order_queue:      "የትዕዛዝ ሁኔታ",            // DRAFT (renamed: Order Status)
    nav_take_order:       "Captain Orders",
    nav_end_of_day:       "የቀኑ ማጠቃለያ (F&B summary)",   // TODO verify
    nav_tabs:             "ክፍት ትዕዛዞች",            // REVIEWED — canonical AM, matches "Open Orders"
    nav_home:             "መነሻ",
    nav_reports:          "ሪፖርቶች",                 // REVIEWED
    nav_adjustments:      "ማስተካከያዎች",
    nav_menu:             "የሜኑ ዕቃዎች",             // DRAFT (renamed: Menu Items; "ሜኑ" is the reviewed canonical for Menu)
    nav_recommended:      "የተመረጡ",                // REVIEWED
    nav_tables:           "ጠረጴዛዎች እና ክፍሎች",      // DRAFT (renamed: Tables & Rooms)
    nav_qr_codes:         "QR ኮዶች",                // DRAFT (new)
    nav_staff:            "የሠራተኞች መለያዎች",        // DRAFT (renamed: Staff Accounts)
    nav_assignments:      "የአስተናጋጅ ምደባ",          // DRAFT (renamed: Waiter Assignment)
    nav_ratings:          "ግምገማዎች እና አስተያየት",     // DRAFT (renamed: Ratings & Feedback)
    nav_admin:            "አስተዳዳሪ",                // REVIEWED
    nav_logout:           "ውጣ",                    // REVIEWED
    my_tables_today:      "የእርስዎ ጠረጴዛዎች ዛሬ",
    no_tables_assigned:   "ዛሬ የተመደቡ ጠረጴዛዎች የሉም",
    ask_manager:          "የሥራ አስኪያጁ ጠረጴዛ እንዲመድብልዎ ይጠይቁ።",
    nav_group_frontdesk:  "የፊት ዴስክ",
    nav_reservations:     "ማስያዣዎች",
    nav_groups:           "የቡድን ማስያዣዎች",
    nav_rooms:            "ክፍሎች",
    nav_housekeeping:     "የክፍል አጽዳት",
    nav_guest_list:       "የእንግዶች ዝርዝር",
    nav_lost_found:       "Lost & Found",             // TODO verify
    nav_maintenance:      "Maintenance",              // TODO verify am
    nav_rooms_report:     "የክፍሎች ሪፖርት",
    nav_room_types:       "የክፍል ዓይነቶች",
    nav_rate_plans:       "የዋጋ ዕቅዶች",
    nav_board:            "ዳሽቦርድ",
    nav_companies:        "ድርጅቶች",
    nav_proformas:        "ፕሮፎርማዎች",
    nav_support:          "እገዛ እና ድጋፍ",
    nav_security:         "ደህንነት",
    nav_privacy:          "የመረጃ ጥበቃ",
    nav_hotel_settings:   "የሆቴል ቅንብሮች",
    nav_activity:         "የእንቅስቃሴ መዝገብ",
    nav_partner_hotels:   "አጋር ሆቴሎች",
    nav_outsourced:       "የውጭ ክፍሎች",
    nav_night_audit:      "የሌሊት ኦዲት",
    nav_finance_reports:  "የፋይናንስ ሪፖርቶች",
    nav_my_till:          "My till",   // TODO verify
    nav_tills:            "Tills",     // TODO verify
    nav_salesperson_report: "Salesperson sales",   // TODO verify
    nav_forex:            "የውጭ ምንዛሪ ሪፖርት",
    nav_events:           "ዝግጅቶችና ግብዣዎች",
    nav_handover:         "የፈረቃ ርክክብ",
    svc_grp_orders:       "ማዘዝ",
    svc_grp_menu:         "ምናሌ",
    svc_grp_tables:       "ጠረጴዛዎች",
    svc_grp_cashier:      "ካሸርና ክፍያ",
    fd_grp_ops:           "ዕለታዊ ሥራ",
    fd_grp_reception:     "አቀባበልና ማስያዣ",
    fd_grp_housekeeping:  "ጽዳትና ጥገና",
    fd_grp_dayclose:      "የቀን ዝግ",
    fd_grp_setup:         "ማዋቀር",
    fd_grp_analytics:     "ትንታኔ",
    nav_reports:          "ሪፖርቶች",
    nav_group_orders:     "የአገልግሎት አስተዳደር",       // DRAFT (renamed: Service Management)
    nav_group_menu:       "የሜኑ አስተዳደር",           // DRAFT (renamed: Menu Management)
    nav_group_tableqr:    "ጠረጴዛ እና QR ማዋቀር",      // DRAFT (new: Table & QR Setup)
    nav_group_team:       "የቡድን አስተዳደር",          // DRAFT (renamed: Team Management)
    nav_group_insights:   "የሥራ አስኪያጅ ግንዛቤዎች",    // DRAFT (renamed: Manager Insights)
    nav_departments:      "ክፍሎች",
    dept_frontdesk:       "የፊት ዴስክ",
    dept_service:         "የምግብ ቤት አገልግሎት",
    dept_services:        "መዝናኛ እና ደህንነት",
    nav_services_catalog: "የአገልግሎት ዝርዝር",
    nav_members:          "አባላት",
    nav_checkin:          "መግቢያ",
    nav_renewals:         "እድሳቶች",
    nav_classes:          "ክፍሎች",
    nav_packages:         "ጥቅሎች",
    nav_membership_plans: "የአገልግሎት ጥቅሎች",   // TODO verify
    nav_service_bookings: "ማስያዣዎች",
    nav_service_schedule: "መርሐግብር",
    nav_resources:        "መርጃዎች",
    dept_kitchen:         "ወጥ ቤት",
    dept_team:            "ቡድን",
    dept_insights:        "ግንዛቤዎች",
    dept_reports:         "ሪፖርቶች",                  // TODO verify
    rpt_grp_close:        "Financial close",         // TODO verify
    rpt_grp_analytics:    "Business analytics",      // TODO verify
    nav_fnb_analytics:    "F&B analytics",           // TODO verify
    nav_business_overview: "Business overview",       // TODO verify
    // Daily routines
    rt_routines:          "ዕለታዊ ተግባራት",
    rt_morning_title:     "የጠዋት ክፍት",
    rt_morning_sub:       "ሆቴሉን ለቀኑ ያዘጋጁ",
    rt_evening_title:     "የቀን መዝጊያ",
    rt_evening_sub:       "ቀኑን በሁሉም ክፍሎች በንጹህ ይዝጉ",
    rt_progress:          "{n} ከ {m} ተጠናቅቋል",
    rt_all_done:          "ተግባሩ ተጠናቅቋል",
    rt_all_done_sub:      "ሁሉም ደረጃ ተከናውኗል። ጥሩ ሥራ።",
    rt_open:              "ክፈት",
    rt_left:              "{n} ቀርቷል",
    rt_back:              "ወደ መነሻ ተመለስ",
    m_rooms:              "ክፍሎች ዝግጁ",
    m_rooms_d:            "የጸዱ ክፍሎች መፈተሻቸውና ዝግጁ መሆናቸውን ያረጋግጡ",
    m_arrivals:          "መግቢያና መውጫ",
    m_arrivals_d:        "ዛሬ የሚገቡና የሚወጡትን ይገምግሙ",
    m_stock:             "የቀኑ ክምችት",
    m_stock_d:           "ከአገልግሎት በፊት ያነሱ ንጥሎችን ይፈትሹ",
    m_menu:              "የሜኑ ዝግጁነት",
    m_menu_d:            "የዛሬ ልዩ ምግቦችንና ያለቁ ንጥሎችን ያዘጋጁ",
    m_staff:             "የፈረቃ ሠራተኞች",
    m_staff_d:           "የዛሬ ምደባዎችን ያረጋግጡ",
    e_tabs:              "ክፍት ደረሰኞችን ዝጋ",
    e_tabs_d:            "ከዛሬ የቀሩ ክፍት ደረሰኞችን ይወራርዱ",
    e_usage:             "አጠቃቀም አረጋግጥ",
    e_usage_d:           "በመጠባበቅ ላይ ያሉ የወጥ ቤት አጠቃቀም መዝገቦችን ያጽድቁ",
    e_review:            "ቅናሾችንና ስረዛዎችን ገምግም",
    e_review_d:          "የዛሬ ቅናሾችንና ስረዛዎችን ይፈትሹ",
    e_guests:            "የእንግዳ ዝርዝር ለፖሊስ",
    e_guests_d:          "የዛሬ ማታ በሆቴል ያሉ እንግዶች ሪፖርት ያትሙ",
    e_report:            "የቀን መጨረሻ ሪፖርት",
    e_report_d:          "የዛሬ የሽያጭ ማጠቃለያ ያሂዱና ያስቀምጡ",

    status_pending:       "በመጠባበቅ",
    status_preparing:     "በዝግጅት",
    status_ready:         "ዝግጁ",
    status_served:        "ቀርቧል",
    status_cancelled:     "ተሰርዟል",
    status_open:          "ክፍት",
    status_closed:        "የተዘጋ",
    tab_active:           "ንቁ",
    tab_scheduled:        "ተያዘ", serve_at: "አቅርብ በ", fire_now: "አሁን ላክ",
    tab_all_today:        "ዛሬ ሁሉም",
    refresh:              "አድስ",
    loading:              "በመጫን ላይ…",
    no_orders_here:       "ምንም ትዕዛዝ የለም።",
    tab_ready_to_run:     "🛎️ ለማቅረብ ዝግጁ",
    no_ready_items:       "እስካሁን ለማቅረብ ዝግጁ የሆነ የለም።",
    signed_in_as:         "የገባ",
    live:                 "ቀጥታ",
    polling:              "በማረጋገጥ",

    btn_add:              "ጨምር",
    btn_remove:           "አስወግድ",
    btn_submit:           "ላክ",
    btn_save:             "አስቀምጥ",
    btn_cancel:           "ሰርዝ",
    btn_confirm:          "አረጋግጥ",
    btn_edit:             "አርትዕ",
    btn_delete:           "አጥፋ",
    btn_back:             "ተመለስ",
    btn_mark_preparing:   "ማዘጋጀት ጀምር",
    btn_mark_ready:       "ዝግጁ ምልክት",
    btn_mark_served:      "ቀርቦ ምልክት",
    btn_close_tab:        "ክፍት ትዕዛዝ ዝጋ",
    btn_submit_order:     "ትዕዛዝ ላክ",
    btn_place_order:      "ትዕዛዝ ስጥ",
    btn_mark_completed:   "ተጠናቀቀ ምልክት",
    receipt:              "ደረሰኝ",
    just_now:             "አሁን",

    table:                "ጠረጴዛ",
    round:                "ዙር",
    order:                "ትዕዛዝ",
    items:                "ዕቃዎች",
    item:                 "ዕቃ",
    total:                "ድምር",
    notes:                "ማስታወሻ",
    note_for_kitchen:     "ለኩሽና ማስታወሻ",
    waiter:               "አስተናጋጅ",
    chef:                 "ሼፍ",
    manager:              "ሥራ አስኪያጅ",
    customer:             "ደንበኛ",
    guest:                "እንግዳ",

    take_order_title:     "በጠረጴዛ ትዕዛዝ ይውሰዱ",
    select_table:         "ጠረጴዛ ይምረጡ",
    search_items:         "ሜኑ ይፈልጉ",
    cart:                 "ቅርጫት",
    cart_empty:           "ቅርጫቱ ባዶ ነው — ለመጨመር ከላይ ዕቃ ይንኩ።",
    add_item_to_order:    "ወደ ትዕዛዝ ጨምር",
    review_and_submit:    "ይከልሱና ላኩ",

    kitchen_queue_title:  "የኩሽና ሰልፍ",
    no_orders_yet:        "እስካሁን ምንም ትዕዛዝ የለም።",
    time_ago_minutes:     "ደቂቃ በፊት",
    time_ago_just_now:    "አሁን",

    tabs_title:           "ክፍት ትዕዛዞች",
    no_open_tabs:         "በአሁኑ ጊዜ ክፍት ትዕዛዝ የለም።",
    new_round:            "አዲስ ዙር",
    running_total:        "ድምር በሂደት",

    // ---- F&B Cost Control — cc_section & cc_nav_variance CONFIRMED by owner
    //      (2026-06-20); remaining cc_* are DRAFT pending native review. ----
    cc_section:           "F&B ወጪ ቁጥጥር",            // CONFIRMED
    cc_nav_ingredients:   "ግብዓቶች",
    cc_nav_recipes:       "የምግብ አሰራር",
    cc_nav_usage:         "የአጠቃቀም መዝገብ",
    cc_nav_variance:      "የባከነ",                    // CONFIRMED
    menueng_title:        "የምናሌ ትንተና",
    fr_title:             "የምግብ ማገገሚያ",
    menueng_subtitle:     "የትኞቹ ምግቦች ትርፍ ያስገኛሉ",
    menueng_intro:        "እያንዳንዱ ምግብ በትርፍ (ዋጋ − የሪሲፒ ወጪ) እና በተወዳጅነት ይመደባል። ኮከቦች ያተርፉና ይሸጣሉ፤ ማረሻ-ፈረሶች ይሸጣሉ ግን ትንሽ ያተርፋሉ፤ እንቆቅልሾች ያተርፋሉ ግን አይሸጡም፤ ውሾች ሁለቱንም አያደርጉም።",
    menueng_avg_margin:   "የምናሌ አማካይ ህዳግ",
    menueng_pop_line:     "የተወዳጅነት መስመር",
    menueng_units:        "ብዛት",
    menueng_dishes:       "ምግቦች",
    menueng_uncosted:     "ሪሲፒ የሌላቸው የተሸጡ ምግቦች (ወጪ ሊሰላ አይችልም፤ አልተካተቱም)",
    menueng_no_rows:      "በዚህ ክልል ውስጥ ሽያጭ የለም።",
    menueng_what_to_do:   "ምን ማድረግ",
    menueng_dish:         "ምግብ",
    menueng_sold:         "ተሸጠ",
    menueng_price:        "ዋጋ",
    menueng_cost:         "የሪሲፒ ወጪ",
    menueng_margin:       "ህዳግ",
    menueng_fcpct:        "ምግብ %",
    menueng_contribution: "አስተዋፅኦ",
    menueng_class:        "ምድብ",
    cc_log_waste:         "ብክነት / መበላሸት መዝግብ",
    cc_qty:               "ብዛት",
    cc_reason:            "ምክንያት",
    cc_log:               "መዝግብ",
    cc_saved:             "ተቀምጧል",
    cc_waste_fill:        "ንጥረ ነገር እና ብዛት ይምረጡ።",
    cc_waste:             "ብክነት",
    cc_waste_spoilage:    "መበላሸት",
    cc_waste_over_prep:   "ከመጠን በላይ ማዘጋጀት",
    cc_waste_trim:        "መከርከም",
    cc_waste_breakage:    "መሰበር",
    cc_waste_staff_meal:  "የሰራተኛ ምግብ",
    cc_waste_comp:        "ነፃ",
    cc_waste_other:       "ሌላ",
    cc_dq_title:          "የመረጃ ጥራት — ቁጥሮች ዝቅ ብለው ሊሆኑ ይችላሉ",
    cc_dq_missing_recipes: "ሪሲፒ የሌላቸው የተሸጡ ምግቦች (እንደ ዜሮ ወጪ ተቆጥረዋል)",
    cc_dq_zero_cost:      "ዜሮ ወጪ ያላቸው የሪሲፒ ንጥረ ነገሮች",
    cc_trend_title:       "የምግብ ወጪ አዝማሚያ — ያለፉት 14 ቀናት",
    cc_stock:             "ክምችት",
    cc_reorder_at:        "እንደገና አዝዝ በ",
    cc_par:               "የሙላት ደረጃ",
    cc_supplier:          "አቅራቢ",
    dept_restaurant:      "ምግብ ቤት",
    dept_wellness:        "ጂም፣ ስፓ እና ጤንነት",
    wel_grp_desk:         "ፊት ዴስክ",
    wel_grp_members:      "አባልነት",
    wel_grp_programs:     "ክፍሎችና አገልግሎቶች",
    dept_inventory:       "የምግብና መጠጥ ወጪ ቁጥጥር",
    rst_grp_service:      "አገልግሎት",
    rst_grp_kitchen:      "ማብሰያ ክፍል",
    inv_grp_setup:        "ማዋቀር",
    inv_grp_stock:        "ግዢና ክምችት",
    inv_grp_analysis:     "ትንተና",
    nav_cashier:          "ገንዘብ ተቀባይ",
    nav_my_code:          "የኔ ኮድ",
    nav_run:              "ለማድረስ ዝግጁ",
    nav_purchasing:       "ግዢ",
    bo_nav:               "ጀርባ ቢሮ",
    dept_backoffice:      "ጀርባ ቢሮ",
    bo_grp_procurement:   "ግዥ",
    bo_grp_stores:        "መጋዘንና ክምችት",
    bo_grp_catalogue:     "ካታሎግ",
    bo_requisitions:      "የግዥ ጥያቄዎች",
    bo_receiving:         "መቀበያ",
    bo_stock:             "በእጅ ያለ ክምችት",
    bo_transfers:         "ዝውውሮች",
    bo_issues:            "መውጫዎች",
    bo_stores:            "መጋዘኖች",
    bo_items:             "እቃዎች",
    bo_settings:          "የማጽደቅ ፖሊሲ",
    nav_stocktake:        "የክምችት ቆጠራ",
    nav_schedule:         "የጊዜ ሰሌዳ",
    cc_name:              "ስም",
    cc_unit:              "መለኪያ",
    cc_category:          "ምድብ",
    cc_optional:          "አማራጭ",
    cc_cost_per_unit:     "ዋጋ / መለኪያ",
    cc_saving:            "በማስቀመጥ ላይ…",
    cc_could_not_load:    "መጫን አልተቻለም።",
    cc_could_not_save:    "ማስቀመጥ አልተቻለም።",
    cc_from:              "ከ",
    cc_to:                "እስከ",
    cc_today:             "ዛሬ",
    cc_yesterday:         "ትናንት",
    cc_last_7_days:       "ያለፉት 7 ቀናት",
    cc_ing_intro:         "የእርስዎ መጋዘንና የአንዱ መለኪያ ዋጋ። ጠቃሚ ምክር፦ የአንድ መለኪያ ዋጋ ያስገቡ (ለምሳሌ 50 ኪሎ ከረጢት በ30,000 ከሆነ የአንድ ኪሎ ዋጋ 600 ነው)።",
    cc_ing_name_ph:       "ለምሳሌ ዶሮ",
    cc_name_unit_required:"ስምና መለኪያ ያስፈልጋሉ።",
    cc_could_not_add:     "መጨመር አልተቻለም።",
    cc_adding:            "በመጨመር ላይ…",
    cc_ingredient:        "ግብዓት",
    cc_no_ingredients:    "እስካሁን ግብዓት የለም — የመጀመሪያውን ከላይ ይጨምሩ።",
    cc_delete_confirm:    "{name}ን ይሰረዝ? የምግብ አሰራር መስመሮቹም ይወገዳሉ።",
    cc_rec_subtitle:      "መደበኛ መጠን",
    cc_rec_need_ings:     "መጀመሪያ በግብዓቶች ገጽ ግብዓቶችን ይጨምሩ፣ ከዚያ እዚህ የምግብ አሰራር ይገንቡ።",
    cc_dish:              "ምግብ",
    cc_no_lines:          "በዚህ የምግብ አሰራር ውስጥ እስካሁን ግብዓት የለም።",
    cc_add_ingredient:    "+ ግብዓት ጨምር",
    cc_dish_cost:         "የምግብ ወጪ",
    cc_menu_price:        "የሜኑ ዋጋ",
    cc_food_cost_pct:     "የምግብ ወጪ %",
    cc_suggested_price:   "የተጠቆመ ዋጋ",
    cc_margin:            "ትርፍ",
    cc_food_target:       "የምግብ ዒላማ %",
    cc_drinks_target:     "የመጠጥ ዒላማ %",
    cc_price_ok:          "ዋጋው ጥሩ ነው",
    cc_price_watch:       "ትንሽ ዝቅተኛ",
    cc_price_high:        "ዋጋው ያነሰ ነው",
    cc_targets_hint:      "የታለመ የምግብ ወጪ %። የተጠቆመ ዋጋ = የምግብ ወጪ ÷ ዒላማ።",
    cc_save_recipe:       "የምግብ አሰራር አስቀምጥ",
    cc_saved_std_cost:    "የተቀመጠ መደበኛ ወጪ፦",
    cc_could_not_load_recipe: "የምግብ አሰራር መጫን አልተቻለም።",
    cc_usage_intro:       "የእያንዳንዱ ግብዓት ምን ያህል በትክክል እንደዋለ ያስገቡ። ተቆጣጣሪው ይህን ከተሸጠው ጋር ያነጻጽራል።",
    cc_day:               "ቀን",
    cc_no_ings_defined:   "እስካሁን የተገለጹ ግብዓቶች የሉም።",
    cc_save_usage:        "አጠቃቀም አስቀምጥ",
    cc_saved_usage_for:   "ለ{date} አጠቃቀም ተቀምጧል።",
    cc_var_subtitle:      "መደበኛ ከትክክለኛ ፍጆታ ጋር",
    cc_var_intro:         "መደበኛ ፍጆታ (የምግብ አሰራር × የተጠናቀቁ ትዕዛዞች) ከሼፉ ከመዘገበው ትክክለኛ ፍጆታ ጋር። ልዩነቱ ብክነት፣ ከመጠን በላይ ማቅረብ ወይም ስርቆት ነው።",
    cc_revenue:           "የሽያጭ ገቢ",
    cc_orders:            "የተጠናቀቁ ትዕዛዞች",
    cc_theo_food:         "መደበኛ ፍጆታ",
    cc_actual_food:       "ትክክለኛ ፍጆታ",
    cc_gross_profit:      "ጠቅላላ ትርፍ",
    cc_gross_profit_note: "ገቢ ሲቀነስ የምግብ ወጪ — ከደመወዝ፣ ኪራይና ሌሎች ወጪዎች በፊት።",
    cc_nav_issue:           "ለኩሽና የተሰጡ ግብዓቶች መዝገብ",
    cc_issue_intro_ctrl:    "ዛሬ ጠዋት ለኩሽና የሚሰጡትን ጥሬ ዕቃ ይመዝግቡ። ሼፉ የተቀበለውን ያረጋግጣል።",
    cc_issue_intro_chef:    "ዛሬ ከመጋዘን የተቀበሉትን ጥሬ ዕቃ ያረጋግጡ። ከተሰጠው የሚለይ መጠን ካለ ያስተካክሉ።",
    cc_issued:              "ለኩሽና የተሰጠ",
    cc_received:            "የተቀበለ",
    cc_issued_food:         "ለኩሽና የተሰጠ",
    cc_issue_save:          "ወጪ አስቀምጥ",
    cc_issue_confirm:       "ደረሰኝ አረጋግጥ",
    cc_issue_confirm_hint:  "መጠኖቹ እንደተሰጠው ሆነው ይቀመጣሉ — የሚለዩትን ብቻ ይቀይሩ።",
    cc_issue_status_none:   "ገና አልተመዘገበም",
    cc_issue_status_issued: "ተሰጥቷል — የኩሽና ማረጋገጫ በመጠባበቅ ላይ",
    cc_issue_status_confirmed: "በኩሽና ተረጋግጧል",
    cc_issue_none_for_day:  "ለዚህ ቀን ገና ወጪ አልተመዘገበም።",
    cc_issue_saved:         "ወጪ ተቀምጧል",
    cc_issue_confirmed_msg: "ደረሰኝ ተረጋግጧል",
    cc_issue_mismatch:      "ከተሰጠው ይለያል",
    go_nav: "የቡድን ትዕዛዞች (ምግብና መጠጥ)",
    go_intro: "በስልክ ወይም በአካል የተያዙ የበርካታ ሰዎች የምግብ ቤት ትዕዛዞች — አዳራሽ የለም፤ በአንድ ሂሳብ፣ ከኩሽና ዝግጅትና የግዢ ዝርዝር ጋር።",
    go_new: "አዲስ የቡድን ትዕዛዝ",
    go_back: "ወደ ዝርዝር ተመለስ",
    go_name: "የትዕዛዝ ስም",
    go_name_ph: "ለምሳሌ የአዋሽ ባንክ ስብሰባ",
    go_name_required: "የትዕዛዝ ስም ያስፈልጋል።",
    go_organizer: "ድርጅት / ሰው",
    go_organizer_ph: "ትዕዛዙ ለማን እንደሆነ",
    go_contact: "የመገናኛ ሰው",
    go_phone: "ስልክ",
    go_when: "ቀንና ሰዓት",
    go_headcount: "የሰዎች ብዛት",
    go_status: "ሁኔታ",
    go_note: "ማስታወሻ",
    go_dishes: "ምግቦች",
    go_add_dish: "ምግብ ጨምር",
    go_no_dishes: "ገና ምግብ አልተጨመረም።",
    go_remove: "አስወግድ",
    go_total: "የትዕዛዝ ድምር",
    go_prep_title: "የዝግጅትና የግዢ ዝርዝር",
    go_prep_hint: "ለእነዚህ ምግቦች የምግብ አዘገጃጀት (በF&B ወጪ ቁጥጥር) ይጨምሩ — የሚዘጋጁና የሚገዙ ግብዓቶችን መጠን ለማየት።",
    go_prep_save_first: "የዝግጅትና የግዢ ዝርዝር ለማየት ትዕዛዙን ያስቀምጡ።",
    go_ingredient: "ግብዓት",
    go_qty_needed: "የሚያስፈልግ መጠን",
    go_est_cost: "ግምታዊ ወጪ",
    go_prep_total: "ግምታዊ የግብዓት ወጪ",
    go_print: "የግዢ ዝርዝር አትም",
    go_purchase_list_title: "የግዢ / የዝግጅት ዝርዝር",
    go_when: "መቼ",
    go_days: "ቀናት",
    go_organizer: "አስተባባሪ",
    go_print_footer: "በODRA የተዘጋጀ",
    go_popup_blocked: "ይህን ዝርዝር ለማተም እባክዎ ብቅ-ባይ መስኮቶችን ይፍቀዱ።",
    go_save: "አስቀምጥ",
    go_saving: "በማስቀመጥ ላይ…",
    go_saved: "ተቀምጧል",
    go_delete: "ሰርዝ",
    go_cancel: "ይቅር",
    go_confirm_delete: "ይህን የቡድን ትዕዛዝ ይሰረዝ?",
    go_could_not_save: "ማስቀመጥ አልተቻለም።",
    go_could_not_load: "መጫን አልተቻለም።",
    go_none: "ገና የቡድን ትዕዛዝ የለም።",
    go_no_date: "ቀን አልተቀመጠም",
    go_pax: "ሰዎች",
    go_status_draft: "ረቂቅ",
    go_status_confirmed: "ተረጋግጧል",
    go_status_in_prep: "በዝግጅት ላይ",
    go_status_served: "ቀርቧል",
    go_status_closed: "ተዘግቷል",
    go_status_cancelled: "ተሰርዟል",
    go_assign_staff: "የተመደቡ ሠራተኞች (ሼፎች እና አስተናጋጆች)",
    go_no_staff: "መጀመሪያ በሠራተኞች ገጽ ሼፎች ወይም አስተናጋጆች ይጨምሩ።",
    go_none_assigned: "ገና ለእርስዎ የተመደበ የቡድን ትዕዛዝ የለም።",
    go_to_prepare: "የሚዘጋጅ",
    go_start_prep: "ዝግጅት ጀምር",
    go_mark_served: "ቀርቧል ምልክት አድርግ",
    go_refresh: "አድስ",
    go_days: "ቀናት",
    go_add_buffet: "ቡፌ ጨምር",
    go_buffet: "ቡፌ",
    buf_nav: "የቡፌ ጥቅሎች",
    buf_intro: "ለቡድን ትዕዛዞች የቡፌ ጥቅሎች — የአንድ ሰው ዋጋና የተካተተውን ያስቀምጡ። ለእንግዶችና ለካፒቴን ትዕዛዝ አይታይም።",
    buf_new: "አዲስ ቡፌ",
    buf_name: "ስም",
    buf_level: "ደረጃ",
    buf_price: "የአንድ ሰው ዋጋ",
    buf_def: "የተካተተው",
    buf_def_ph: "ለምሳሌ ዋና፡ ዶሮ ወጥ፣ ክትፎ · ትኩስ መጠጦች · ጣፋጭ",
    buf_active: "ንቁ",
    buf_save: "አስቀምጥ",
    buf_saved: "ተቀምጧል",
    buf_none: "ገና የቡፌ ጥቅል የለም።",
    buf_confirm_delete: "ይህን ቡፌ ይሰረዝ?",
    cc_theo_food_pct:     "መደበኛ የምግብ ወጪ %",
    cc_actual_food_pct:   "ትክክለኛ የምግብ ወጪ %",
    cc_variance_cost:     "የባከነ (ወጪ)",
    cc_no_actual_notice:  "ለዚህ ጊዜ ክልል እስካሁን ትክክለኛ አጠቃቀም አልተመዘገበም — ሼፉ በ‘የአጠቃቀም መዝገብ’ እንዲያስገባ ይጠይቁ። መደበኛ ቁጥሮች ይታያሉ።",
    cc_should_theory:     "መደበኛ ፍጆታ",
    cc_actual:            "ትክክለኛ ፍጆታ",
    cc_returned:          "የተመለሰ",
    cc_loss:              "ያልተመዘገበ ብክነት",
    cc_confirm:           "አረጋግጥ",
    cc_confirmed:         "የተረጋገጠ",
    cc_pending:           "በመጠባበቅ ላይ",
    cc_confirm_usage:     "ቀኑን አረጋግጥ",
    cc_shift:             "ፈረቃ",
    cc_received:          "የተቀበለ",
    cc_used:              "የዋለ",
    cc_handover:          "ማስረከቢያ",
    cc_accept_handover:   "ማስረከቢያ ተቀበል",
    cc_shift_hint:        "ፈረቃ 1 በጠዋቱ ርክክብ ይጀምራል፤ እያንዳንዱ ቀጣይ ፈረቃ በቀድሞው ፈረቃ ማስረከቢያ ይጀምራል። የመጨረሻው ፈረቃ ማስረከቢያ ወደ መጋዘን ተመላሽ ነው።",
    cc_return_to_store:   "ወደ መጋዘን ተመላሽ",
    cc_closing_shift:     "የመዝጊያ ፈረቃ (ወደ መጋዘን ተመላሽ)",
    cc_overview:          "አጠቃላይ እይታ",
    cc_home_sub:          "የምግብ ወጪ ዳሽቦርድዎ — የዛሬው በጨረፍታ።",
    cc_pending_confirm:   "የዛሬው አጠቃቀም ማረጋገጫዎን በመጠበቅ ላይ ነው",
    cc_quick_actions:     "ፈጣን እርምጃዎች",
    cc_cost_impact:       "የወጪ ተጽዕኖ",
    cc_var_no_rows:       "በዚህ ጊዜ ክልል የግብዓት አጠቃቀም የለም (በተሸጡ ምግቦች ላይ የምግብ አሰራር የለም?)።",
  },

  // ===================================================================
  // AFAAN OROMO (om) — review-sheet corrections applied 2026-06-05
  // (Tarree Ajajoota, Ajaja Baname, Kuusaa, Ida'ama, Fe'aat jira…,
  // Hoogganaa, status terms). Items marked "DRAFT" are new/renamed
  // labels pending expert review.
  // ===================================================================
  om: {
    nav_order_queue:      "Haala Ajajoota",            // DRAFT (renamed: Order Status; reviewed term for queue was "Tarree Ajajoota")
    nav_take_order:       "Captain Orders",
    nav_end_of_day:       "Cuunfaa Guyyaa (F&B summary)",   // TODO verify
    nav_tabs:             "Ajajoota Baname",            // REVIEWED (was "Tabii")
    nav_home:             "Mana",
    nav_reports:          "Gabaasaalee",             // REVIEWED
    nav_adjustments:      "Sirreeffamawwan",
    nav_menu:             "Meeshaalee Menuu",        // DRAFT (renamed: Menu Items)
    nav_recommended:      "Kan Filataman",               // REVIEWED
    nav_tables:           "Minjaalota fi Kutaalee",  // DRAFT (renamed: Tables & Rooms)
    nav_qr_codes:         "Koodii QR",               // DRAFT (new)
    nav_staff:            "Akkaawuntii Hojjettootaa", // DRAFT (renamed: Staff Accounts)
    nav_assignments:      "Ramaddii Tajaajiltoota",     // DRAFT (renamed: Waiter Assignment)
    nav_ratings:          "Madaallii fi Yaada",      // DRAFT (renamed: Ratings & Feedback)
    nav_admin:            "Hoogganaa",               // REVIEWED (was "Bulchaa")
    nav_logout:           "Ba'i",                    // REVIEWED
    my_tables_today:      "Minjaalota guyyaa har'aa itti ramadamtan",
    no_tables_assigned:   "Har'a minjaalonni isiniif ramadame hin jiru",
    ask_manager:          "Hoogganaa minjaala akka isiniif ramaduuf gaafadhaa.",
    nav_group_frontdesk:  "Fuula Duraa",
    nav_reservations:     "Qabannoowwan",
    nav_groups:           "Qabannoo garee",
    nav_rooms:            "Kutaalee",
    nav_housekeeping:     "Qulqullina kutaa",
    nav_guest_list:       "Tarree keessummoota",
    nav_lost_found:       "Lost & Found",             // TODO verify
    nav_maintenance:      "Maintenance",              // TODO verify om
    nav_rooms_report:     "Gabaasa kutaalee",
    nav_room_types:       "Gosoota kutaa",
    nav_rate_plans:       "Karoora gatii",
    nav_board:            "Gabatee",
    nav_companies:        "Dhaabbilee",
    nav_proformas:        "Proformaa",
    nav_support:          "Gargaarsa & Deeggarsa",
    nav_security:         "Nageenya",
    nav_privacy:          "Eegumsa daataa",
    nav_hotel_settings:   "Qindaa'ina hoteelaa",
    nav_activity:         "Galmee sochii",
    nav_partner_hotels:   "Hoteelota michuu",
    nav_outsourced:       "Kutaalee ala",
    nav_night_audit:      "Odiitii halkanii",
    nav_finance_reports:  "Gabaasota faayinaansii",
    nav_my_till:          "My till",   // TODO verify
    nav_tills:            "Tills",     // TODO verify
    nav_salesperson_report: "Salesperson sales",   // TODO verify
    nav_forex:            "Gabaasa sharafaa",
    nav_events:           "Sagantaalee fi Cidha",
    nav_handover:         "Dabarsa gaggeeffannaa",
    nav_reports:          "Gabaasota",
    svc_grp_orders:       "Ajajuu",
    svc_grp_menu:         "Menu",
    svc_grp_tables:       "Minjaala",
    svc_grp_cashier:      "Kaasharaa fi kaffaltii",
    fd_grp_ops:           "Hojii guyyaa",
    fd_grp_reception:     "Simannaa fi qabannoo",
    fd_grp_housekeeping:  "Qulqullinaa fi suphaa",
    fd_grp_dayclose:      "Cufaa guyyaa",
    fd_grp_setup:         "Qindeessuu",
    fd_grp_analytics:     "Xiinxala",
    nav_group_orders:     "Bulchiinsa Tajaajilaa",   // DRAFT (renamed: Service Management)
    nav_group_menu:       "Qindoomiinsa Menuu",        // DRAFT (renamed: Menu Management)
    nav_group_tableqr:    "Minjaalaa fi QR Sirreessu", // DRAFT (new: Table & QR Setup)
    nav_group_team:       "Bulchiinsa Hojjattootaa",        // DRAFT (renamed: Team Management)
    nav_group_insights:   "Hubannoo Hoogganaa",      // DRAFT (renamed: Manager Insights)
    nav_departments:      "Kutaalee",
    dept_frontdesk:       "Fuula Duraa",
    dept_service:         "Tajaajila Mana Nyaataa",
    dept_services:        "Bashannanaa fi Fayyaa",
    nav_services_catalog: "Kanaalii tajaajilaa",
    nav_members:          "Miseensota",
    nav_checkin:          "Check-in",
    nav_renewals:         "Renewals",
    nav_classes:          "Classes",
    nav_packages:         "Packages",
    nav_membership_plans: "Tuuta tajaajilaa",   // TODO verify
    nav_service_bookings: "Qabannoowwan",
    nav_service_schedule: "Sagantaa",
    nav_resources:        "Qabeenyawwan",
    dept_kitchen:         "Kutaa Nyaataa",
    dept_team:            "Garee",
    dept_insights:        "Hubannoowwan",
    dept_reports:         "Gabaasaalee",             // TODO verify
    rpt_grp_close:        "Financial close",         // TODO verify
    rpt_grp_analytics:    "Business analytics",      // TODO verify
    nav_fnb_analytics:    "F&B analytics",           // TODO verify
    nav_business_overview: "Business overview",       // TODO verify
    // Daily routines
    rt_routines:          "Sochii guyyaa",
    rt_morning_title:     "Banaa ganamaa",
    rt_morning_sub:       "Hoteelicha guyyaaf qopheessi",
    rt_evening_title:     "Xumura guyyaa",
    rt_evening_sub:       "Guyyaa kutaalee hunda keessatti qulqulluu cufi",
    rt_progress:          "{n} kan {m} xumurame",
    rt_all_done:          "Sochiin xumurame",
    rt_all_done_sub:      "Tarkaanfiin hundi raawwatame. Hojii gaarii.",
    rt_open:              "Bani",
    rt_left:              "{n} hafe",
    rt_back:              "Gara manaa deebi'i",
    m_rooms:              "Kutaaleen qophii",
    m_rooms_d:            "Kutaaleen qulqullaa'an sakatta'amuu fi qophii ta'uu mirkaneessi",
    m_arrivals:          "Galuu fi ba'uu",
    m_arrivals_d:        "Har'a eenyu akka galuu fi ba'u ilaali",
    m_stock:             "Kuusaa guyyaa",
    m_stock_d:           "Tajaajila dura meeshaalee xiqqaatan ilaali",
    m_menu:              "Argama menuu",
    m_menu_d:            "Nyaata addaa fi kan dhuman har'aa qopheessi",
    m_staff:             "Hojjettoota gara hojii",
    m_staff_d:           "Ramaddii har'aa mirkaneessi",
    e_tabs:              "Nagahewwan banaa cufi",
    e_tabs_d:            "Nagahewwan har'aa banaa jiran xumuri",
    e_usage:             "Itti fayyadama mirkaneessi",
    e_usage_d:           "Galmeewwan itti fayyadama kutaa nyaataa eegaman mirkaneessi",
    e_review:            "Hir'isaa fi haquu gamaggami",
    e_review_d:          "Hir'isaa fi haquu har'aa ilaali",
    e_guests:            "Tarree keessummaa poolisiif",
    e_guests_d:          "Gabaasa keessummoota hoteela keessa jiran halkan har'aa maxxansi",
    e_report:            "Gabaasa xumura guyyaa",
    e_report_d:          "Cuunfaa gurgurtaa har'aa qabiisaa fi galmeessi",

    status_pending:       "Tarre irra jira",         // REVIEWED (was "Eegamaa")
    status_preparing:     "Qophii irra jira",        // REVIEWED (was "Qophaa'aa")
    status_ready:         "Qophaa'eera",             // REVIEWED
    status_served:        "Dhiyaateera",             // REVIEWED
    status_cancelled:     "Haqame",                  // REVIEWED
    status_open:          "Baname",                  // REVIEWED (was "Banaa")
    status_closed:        "Cufame",                  // REVIEWED (was "Cufaa")
    tab_active:           "Sosso'aa",
    tab_scheduled:        "Ergaa yeroo", serve_at: "Dhiheessi", fire_now: "Amma ergi",
    tab_all_today:        "Har'a hundi",
    refresh:              "Haaromsi",
    loading:              "Fe'aat jira…",            // REVIEWED (was "Fe'aa…")
    no_orders_here:       "Ajaja hin jiru.",
    tab_ready_to_run:     "🛎️ Dhiyeessuuf qophii",
    no_ready_items:       "Hanga ammaa kan dhiyeessuuf qophaa'e hin jiru.",
    signed_in_as:         "Kan seene",
    live:                 "kallattii",
    polling:              "qoraa",

    btn_add:              "Dabali",
    btn_remove:           "Haqi",
    btn_submit:           "Ergi",
    btn_save:             "Olkaa'i",
    btn_cancel:           "Dhiisi",
    btn_confirm:          "Mirkaneessi",
    btn_edit:             "Sirreessi",
    btn_delete:           "Balleessi",
    btn_back:             "Deebi'i",
    btn_mark_preparing:   "Qopheessuu jalqabi",
    btn_mark_ready:       "Akka qophaa'e mallatteessi",
    btn_mark_served:      "Akka dhiyaate mallatteessi",
    btn_close_tab:        "Ajaja baname cufi",       // updated to match "Open Orders" rename
    btn_submit_order:     "Ajaja Ergi",
    btn_place_order:      "Ajaja Kaayi",
    btn_mark_completed:   "Akka xumurame mallatteessi",
    receipt:              "Nagahee",
    just_now:             "Amma",

    table:                "Minjaala",
    round:                "Marsaa",
    order:                "Ajaja",
    items:                "Meeshaalee",
    item:                 "Meeshaa",
    total:                "Ida'ama",                 // REVIEWED (was "Walumaa galatti")
    notes:                "Yaadannoo",
    note_for_kitchen:     "Yaadannoo Kushiinadhaaf",
    waiter:               "Tajaajilaa",
    chef:                 "Cheef",
    manager:              "Hoogganaa",               // REVIEWED (was "Bulchaa")
    customer:             "Maamila",
    guest:                "Keessummeessaa",

    take_order_title:     "Minjaala irratti ajaja fudhadhu",
    select_table:         "Minjaala filadhu",
    search_items:         "Menuu barbaadi",
    cart:                 "Kuusaa",                  // REVIEWED (was "Konkolaataa")
    cart_empty:           "Kuusichi duwwaa dha — dabaluuf meeshaa olii tuqi.", // REVIEWED
    add_item_to_order:    "Gara Ajajaatti Dabali",
    review_and_submit:    "Ilaaliitii ergi",

    kitchen_queue_title:  "Tarree Kuusaa",
    no_orders_yet:        "Hanga amma homaa hin ajajne.",
    time_ago_minutes:     "daqiiqaa dura",
    time_ago_just_now:    "amma",

    tabs_title:           "Ajaja Baname",            // REVIEWED (was "Tabii Banaa")
    no_open_tabs:         "Yeroo ammaa ajajni banaan hin jiru.", // REVIEWED
    new_round:            "Marsaa Haaraa",
    running_total:        "Ida'ama Adeemaa",         // updated for reviewed "Ida'ama"

    // ---- F&B Cost Control — cc_section keeps "F&B"; cc_nav_variance CONFIRMED
    //      by owner (2026-06-20); remaining cc_* are DRAFT, native review pending.
    cc_section:           "F&B To'annoo Baasii",
    cc_nav_ingredients:   "Wantoota Nyaataa",
    cc_nav_recipes:       "Akkaata Itti Qopheessan",  // CONFIRMED
    cc_nav_usage:         "Galmee Akkaata Fayyadamiinsaa", // CONFIRMED
    cc_nav_variance:      "Kan Qisaasa'ee",           // CONFIRMED
    menueng_title:        "Xiinxala menyuu",
    fr_title:             "Deebisa nyaataa",
    menueng_subtitle:     "nyaanni kamtu bu'aa argamsiisa",
    menueng_intro:        "Nyaanni hundi bu'aa (gatii − baasii resiipii) fi jaalatamummaadhaan ramadama. Urjiiwwan bu'aa argatanii ni gurguramu; fardeen qotiisaa ni gurguramu garuu bu'aa xiqqoo argatu; qaaddoowwan bu'aa argatu garuu hin gurguramne; saroonni lachuu hin godhan.",
    menueng_avg_margin:   "Marjiinii giddu-galeessa menyuu",
    menueng_pop_line:     "sarara jaalatamummaa",
    menueng_units:        "baay'ina",
    menueng_dishes:       "nyaatawwan",
    menueng_uncosted:     "Nyaatawwan gurguraman resiipii hin qabne (baasiin hin herregamu, hin dabalamne)",
    menueng_no_rows:      "Daangaa kana keessatti gurgurtaan hin jiru.",
    menueng_what_to_do:   "Maal gochuu",
    menueng_dish:         "Nyaata",
    menueng_sold:         "Gurgurame",
    menueng_price:        "Gatii",
    menueng_cost:         "Baasii resiipii",
    menueng_margin:       "Marjiinii",
    menueng_fcpct:        "Nyaata %",
    menueng_contribution: "Gumaacha",
    menueng_class:        "Ramaddii",
    cc_log_waste:         "Balaa / badii galmeessi",
    cc_qty:               "Baay'ina",
    cc_reason:            "Sababa",
    cc_log:               "Galmeessi",
    cc_saved:             "Olkaa'ame",
    cc_waste_fill:        "Wanta nyaataa fi baay'ina filadhu.",
    cc_waste:             "Balaa",
    cc_waste_spoilage:    "Badii",
    cc_waste_over_prep:   "Qophii hedduu",
    cc_waste_trim:        "Muruu",
    cc_waste_breakage:    "Cabuu",
    cc_waste_staff_meal:  "Nyaata hojjetaa",
    cc_waste_comp:        "Bilisa",
    cc_waste_other:       "Kan biraa",
    cc_dq_title:          "Qulqullina daataa — lakkoofsonni gadi bu'aa ta'uu danda'u",
    cc_dq_missing_recipes: "Nyaatawwan gurguraman resiipii hin qabne (baasii zeeroo ta'anii lakkaa'aman)",
    cc_dq_zero_cost:      "Wantoota nyaataa resiipii baasii zeeroo qaban",
    cc_trend_title:       "Toftaa baasii nyaataa — guyyoota 14 darban",
    cc_stock:             "Kuusaa",
    cc_reorder_at:        "Ammas ajaji",
    cc_par:               "Sadarkaa guutuu",
    cc_supplier:          "Dhiyeessaa",
    dept_restaurant:      "Mana nyaataa",
    dept_wellness:        "Jimii, Spaa fi Fayyaa",
    wel_grp_desk:         "Fuula duraa",
    wel_grp_members:      "Miseensummaa",
    wel_grp_programs:     "Barnootaa fi tajaajila",
    dept_inventory:       "To'annoo Baasii Nyaataa fi Dhugaatii",
    rst_grp_service:      "Tajaajila",
    rst_grp_kitchen:      "Kushina",
    inv_grp_setup:        "Qindaa'ina",
    inv_grp_stock:        "Bittaa fi kuusaa",
    inv_grp_analysis:     "Xiinxala",
    nav_cashier:          "Kaasheera",
    nav_my_code:          "Koodii koo",
    nav_run:              "Geessuuf qophii",
    nav_purchasing:       "Bittaa",
    bo_nav:               "Waajjira Duubaa",
    dept_backoffice:      "Waajjira duubaa",
    bo_grp_procurement:   "Bitannaa",
    bo_grp_stores:        "Kuusaa fi stooki",
    bo_grp_catalogue:     "Kataaloogii",
    bo_requisitions:      "Gaaffiilee bitannaa",
    bo_receiving:         "Fudhannaa",
    bo_stock:             "Stooki harka jiru",
    bo_transfers:         "Dabarsaalee",
    bo_issues:            "Baʼuu",
    bo_stores:            "Kuusaalee",
    bo_items:             "Meeshaalee",
    bo_settings:          "Imaammata mirkaneessaa",
    nav_stocktake:        "Lakkoofsa kuusaa",
    nav_schedule:         "Sagantaa",
    cc_name:              "Maqaa",
    cc_unit:              "Safara",
    cc_category:          "Ramaddii",
    cc_optional:          "filannoo",
    cc_cost_per_unit:     "Gatii / safara",
    cc_saving:            "Olkaa'aa jira…",
    cc_could_not_load:    "Fe'uu hin dandeenye.",
    cc_could_not_save:    "Olkaa'uu hin dandeenye.",
    cc_from:              "Irraa",
    cc_to:                "Hanga",
    cc_today:             "Har'a",
    cc_yesterday:         "Kaleessa",
    cc_last_7_days:       "Guyyoota 7 darban",
    cc_ing_intro:         "Mankuusaa keessanii fi gatii safara tokkoo. Gorsa: gatii safara tokkoo galchaa (fkn. boorsaa kg 50 birrii 30,000 yoo ta'e, gatiin kg tokkoo 600 dha).",
    cc_ing_name_ph:       "fkn. Lukuu",
    cc_name_unit_required:"Maqaa fi safarri barbaachisu.",
    cc_could_not_add:     "Dabaluu hin dandeenye.",
    cc_adding:            "Dabalaa jira…",
    cc_ingredient:        "Wanta Nyaataa",
    cc_no_ingredients:    "Hanga ammaa wanti hin jiru — isa jalqabaa olitti dabali.",
    cc_delete_confirm:    "{name} haqi? Sararoonni qopheessa nyaataa isaas ni haqamu.",
    cc_rec_subtitle:      "hamma idilee",
    cc_rec_need_ings:     "Duraan fuula Wantootaa irratti wantoota dabali, achumaan as qopheessa nyaataa ijaari.",
    cc_dish:              "Nyaata",
    cc_no_lines:          "Qopheessa kana keessa hanga ammaa wanti hin jiru.",
    cc_add_ingredient:    "+ Wanta dabali",
    cc_dish_cost:         "Baasii Nyaataa",
    cc_menu_price:        "Gatii Menuu",
    cc_food_cost_pct:     "Dhibbeentaa Baasii Nyaataa",
    cc_suggested_price:   "Gatii yaadame",
    cc_margin:            "Buʼaa",
    cc_food_target:       "Kaayyoo nyaataa %",
    cc_drinks_target:     "Kaayyoo dhugaatii %",
    cc_price_ok:          "Gatiin gaarii dha",
    cc_price_watch:       "Xiqqoo gadi",
    cc_price_high:        "Gatiin gadi bu'aa dha",
    cc_targets_hint:      "Kaayyoo dhibbeentaa baasii nyaataa. Gatii yaadame = baasii ÷ kaayyoo.",
    cc_save_recipe:       "Qopheessa olkaa'i",
    cc_saved_std_cost:    "Baasii idilee olkaa'ame:",
    cc_could_not_load_recipe: "Qopheessa nyaataa fe'uu hin dandeenye.",
    cc_usage_intro:       "Wanti tokkoon tokkoon isaa hammam akka itti fayyadame galchi. To'ataan kana waan gurgurame waliin wal bira qaba.",
    cc_day:               "Guyyaa",
    cc_no_ings_defined:   "Hanga ammaa wantootni hin ibsamne.",
    cc_save_usage:        "Fayyadama olkaa'i",
    cc_saved_usage_for:   "Fayyadamni {date} olkaa'ame.",
    cc_var_subtitle:      "idilee vs dhugaa",
    cc_var_intro:         "Baasii idilee (qopheessa × ajajawwan xumuraman) baasii dhugaa cheefiin galmeesse waliin. Garaagarummaan kun gatii, hamma ol'aanaa, yookiin hatuu agarsiisa.",
    cc_revenue:           "Galii gurgurtaa",
    cc_orders:            "Ajajawwan xumuraman",
    cc_theo_food:         "Baasii Idilee",
    cc_actual_food:       "Baasii Dhugaa",
    cc_gross_profit:      "Bu'aa waliigalaa",
    cc_gross_profit_note: "Galii irraa baasii nyaataa hir'isuu — mindaa, kiraa fi baasii biroo dura.",
    cc_nav_issue:           "Galmee Wantoota Nyaataa Kushiinaaf Kennaman",
    cc_issue_intro_ctrl:    "Wantoota har'a ganama kushinaaf kennitu galmeessi. Cheef wanta fudhatame ni mirkaneessa.",
    cc_issue_intro_chef:    "Wantoota har'a mankuusaa irraa fudhatan mirkaneessi. Baay'ina kenname irraa adda ta'e jijjiiri.",
    cc_issued:              "Kan kushiinaaf kenname",
    cc_received:            "Kan fudhatame",
    cc_issued_food:         "Kan kushiinaaf kenname",
    cc_issue_save:          "Kennaa olkaa'i",
    cc_issue_confirm:       "Fudhannaa mirkaneessi",
    cc_issue_confirm_hint:  "Baay'inni akkuma kennametti taa'a — kan adda ta'e qofa jijjiiri.",
    cc_issue_status_none:   "Hin galmoofne",
    cc_issue_status_issued: "Kennameera — mirkaneeffannaa kushinaa eeggachaa",
    cc_issue_status_confirmed: "Kushinaadhaan mirkanaa'e",
    cc_issue_none_for_day:  "Guyyaa kanaaf kennaan hin galmoofne.",
    cc_issue_saved:         "Kennaan olkaa'ame",
    cc_issue_confirmed_msg: "Fudhannaan mirkanaa'e",
    cc_issue_mismatch:      "kan kenname irraa adda",
    go_nav: "Ajaja Garee (Nyaataa fi Dhugaatii)",
    go_intro: "Ajajawwan mana nyaataa nama baay'ee bilbilaan ykn dhuunfaan galmaa'an — galmi hin jiru; herrega tokkoon, tarree qophii fi bittaa kushinaa waliin.",
    go_new: "Ajaja garee haaraa",
    go_back: "Gara tarreetti deebi'i",
    go_name: "Maqaa ajaja",
    go_name_ph: "fkn. walgahii Baankii Awaash",
    go_name_required: "Maqaan ajaja barbaachisaadha.",
    go_organizer: "Dhaabbata / nama",
    go_organizer_ph: "Ajajni eenyuuf akka ta'e",
    go_contact: "Nama quunnamtii",
    go_phone: "Bilbila",
    go_when: "Guyyaa fi sa'aatii",
    go_headcount: "Baay'ina namaa",
    go_status: "Haala",
    go_note: "Yaadannoo",
    go_dishes: "Nyaatawwan",
    go_add_dish: "Nyaata dabali",
    go_no_dishes: "Hanga ammaa nyaanni hin dabalamne.",
    go_remove: "Haqi",
    go_total: "Ida'ama ajaja",
    go_prep_title: "Tarree qophii fi bittaa",
    go_prep_hint: "Nyaatawwan kanaaf akkaata qophii (F&B To'annoo Baasii keessatti) dabali — baay'ina wantoota qophaa'anii fi bitamanii arguuf.",
    go_prep_save_first: "Tarree qophii fi bittaa arguuf ajaja olkaa'i.",
    go_ingredient: "Wanta nyaataa",
    go_qty_needed: "Baay'ina barbaachisu",
    go_est_cost: "Baasii tilmaamaa",
    go_prep_total: "Baasii wantootaa tilmaamaa",
    go_print: "Tarree bittaa maxxansi",
    go_purchase_list_title: "Tarree Bittaa / Qophii",
    go_when: "Yeroo",
    go_days: "Guyyoota",
    go_organizer: "Qindeessaa",
    go_print_footer: "ODRA'n qophaa'e",
    go_popup_blocked: "Tarree kana maxxansuuf maaloo foddaa balballoomfataa hayyami.",
    go_save: "Olkaa'i",
    go_saving: "Olkaa'aa jira…",
    go_saved: "Olkaa'ame",
    go_delete: "Haqi",
    go_cancel: "Dhiisi",
    go_confirm_delete: "Ajaja garee kana haquu?",
    go_could_not_save: "Olkaa'uu hin dandeenye.",
    go_could_not_load: "Fe'uu hin dandeenye.",
    go_none: "Hanga ammaa ajajni garee hin jiru.",
    go_no_date: "Guyyaan hin qindaa'in",
    go_pax: "nama",
    go_status_draft: "Wixinee",
    go_status_confirmed: "Mirkanaa'e",
    go_status_in_prep: "Qophii irra",
    go_status_served: "Dhiyaate",
    go_status_closed: "Cufame",
    go_status_cancelled: "Haqame",
    go_assign_staff: "Hojjettoota ramadaman (cheefota fi tajaajiltoota)",
    go_no_staff: "Duraan fuula Hojjettootaa irratti cheefota yookaan tajaajiltoota dabali.",
    go_none_assigned: "Hanga ammaa ajajni garee siif hin ramadamne.",
    go_to_prepare: "Kan qophaa'u",
    go_start_prep: "Qophii jalqabi",
    go_mark_served: "Dhiyaate jedhii mallatteessi",
    go_refresh: "Haaromsi",
    go_days: "Guyyoota",
    go_add_buffet: "Buffet dabali",
    go_buffet: "Buffet",
    buf_nav: "Pakeejii Buffet",
    buf_intro: "Pakeejii buffet ajaja gareef — gatii nama tokkoo fi wanta keessatti hammatame qindeessi. Keessummootaa fi Captain Order irraa dhokfame.",
    buf_new: "Buffet haaraa",
    buf_name: "Maqaa",
    buf_level: "Sadarkaa",
    buf_price: "Gatii nama tokkoo",
    buf_def: "Wanti keessatti hammatame",
    buf_def_ph: "fkn. Ijoo: Doro Wat, Kitfo · Dhugaatii ho'aa · Mi'aawaa",
    buf_active: "Kan hojjetu",
    buf_save: "Olkaa'i",
    buf_saved: "Olkaa'ame",
    buf_none: "Hanga ammaa pakeejiin buffet hin jiru.",
    buf_confirm_delete: "Buffet kana haquu?",
    cc_theo_food_pct:     "Dhibbeentaa baasii nyaataa idilee",
    cc_actual_food_pct:   "Dhibbeentaa baasii nyaataa dhugaa",
    cc_variance_cost:     "Kan Qisaasa'ee (baasii)",
    cc_no_actual_notice:  "Yeroo kanaaf hanga ammaa fayyadamni dhugaa hin galmoofne — cheefiin ‘Galmee Fayyadamaa’ irratti akka galchu gaafadhu. Lakkoofsi idilee garuu ni mul'ata.",
    cc_should_theory:     "Baasii Idilee",
    cc_actual:            "Baasii Dhugaa",
    cc_returned:          "Kan deebi'e",
    cc_loss:              "Baduu hin galmoofne",
    cc_confirm:           "Mirkaneessi",
    cc_confirmed:         "Mirkanaa'e",
    cc_pending:           "Eegaa jira",
    cc_confirm_usage:     "Guyyaa mirkaneessi",
    cc_shift:             "Shiftii",
    cc_received:          "Kan fudhate",
    cc_used:              "Kan fayyadame",
    cc_handover:          "Dabarsaa",
    cc_accept_handover:   "Dabarsaa fudhadhu",
    cc_shift_hint:        "Shiftiin 1 kennaa ganamaatiin eegala; shiftiin itti aanu hundi dabarsaa shiftii duraatiin eegala. Dabarsaan shiftii dhumaa gara mankuusaatti deebi'uudha.",
    cc_return_to_store:   "Gara mankuusaatti deebisi",
    cc_closing_shift:     "Shiftii xumuraa (mankuusaatti deebisi)",
    cc_overview:          "Ilaalcha waliigalaa",
    cc_home_sub:          "Daashboordii baasii nyaataa kee — kan har'aa gabaabaatti.",
    cc_pending_confirm:   "Fayyadamni har'aa mirkaneessa kee eeggachaa jira",
    cc_quick_actions:     "Gochaalee ariifachiisaa",
    cc_cost_impact:       "Dhiibbaa baasii",
    cc_var_no_rows:       "Yeroo kana keessa fayyadamni wantaa hin jiru (nyaata gurgurame irratti qopheessi hin jiru?).",
  },

  // ===================================================================
  // TIGRINYA (ti) — MACHINE DRAFT 2026-06-05, full set. Drafted from
  // standard hospitality Tigrinya; native-speaker review pending.
  // Every value below needs verification before launch to TI tenants.
  // ===================================================================
  ti: {
    nav_order_queue:      "ኩነታት ትእዛዝ",
    nav_take_order:       "Captain Orders",
    nav_end_of_day:       "ናይ መዓልቲ ጸብጻብ (F&B summary)",   // TODO verify
    nav_tabs:             "ክፉት ትእዛዛት",
    nav_reports:          "ጸብጻባት",
    dept_reports:         "ጸብጻባት",                  // TODO verify
    rpt_grp_close:        "Financial close",         // TODO verify
    rpt_grp_analytics:    "Business analytics",      // TODO verify
    nav_fnb_analytics:    "F&B analytics",           // TODO verify
    nav_business_overview: "Business overview",       // TODO verify
    nav_menu:             "ናይ ሜኑ ኣቕሑ",
    nav_recommended:      "ዝምከሩ",
    nav_tables:           "ጠረጴዛታትን ክፍልታትን",
    nav_qr_codes:         "QR ኮዳት",
    nav_staff:            "ሕሳባት ሰራሕተኛታት",
    nav_assignments:      "ምደባ ኣሳለፍቲ",
    nav_ratings:          "ገምጋማትን ርእይቶን",
    nav_admin:            "ኣመሓዳሪ",
    nav_logout:           "ውጻእ",
    my_tables_today:      "ናይ ሎሚ ጠረጴዛታትካ",
    no_tables_assigned:   "ሎሚ ዝተመደበ ጠረጴዛ የለን",
    ask_manager:          "ኣካያዲ ስራሕ ጠረጴዛ ክምድበልካ ሕተት።",
    nav_group_orders:     "ምሕደራ ኣገልግሎት",
    nav_group_menu:       "ምሕደራ ሜኑ",
    nav_group_tableqr:    "ምድላው ጠረጴዛን QRን",
    nav_group_team:       "ምሕደራ ጋንታ",
    nav_group_insights:   "ግንዛበታት ኣካያዲ",

    status_pending:       "ኣብ ምጽባይ",
    status_preparing:     "ኣብ ምድላው",
    status_ready:         "ድሉው",
    status_served:        "ቀሪቡ",
    status_cancelled:     "ተሰሪዙ",
    status_open:          "ክፉት",
    status_closed:        "ዕጹው",
    tab_active:           "ንጡፍ",
    tab_all_today:        "ሎሚ ኩሉ",
    refresh:              "ኣሐድስ",
    loading:              "ይጽዕን ኣሎ…",
    no_orders_here:       "ኣብዚ ትእዛዝ የለን።",
    signed_in_as:         "ዝኣተወ",
    live:                 "ቀጥታ",
    polling:              "ይፍትሽ",

    btn_add:              "ወስኽ",
    btn_remove:           "ኣልግስ",
    btn_submit:           "ለኣኽ",
    btn_save:             "ዓቅብ",
    btn_cancel:           "ሰርዝ",
    btn_confirm:          "ኣረጋግጽ",
    btn_edit:             "ኣርም",
    btn_delete:           "ደምስስ",
    btn_back:             "ተመለስ",
    btn_mark_preparing:   "ምድላው ጀምር",
    btn_mark_ready:       "ድሉው ምልክት ግበር",
    btn_mark_served:      "ከም ዝቐረበ ምልክት ግበር",
    btn_close_tab:        "ክፉት ትእዛዝ ዕጾ",
    btn_submit_order:     "ትእዛዝ ለኣኽ",
    btn_place_order:      "ትእዛዝ ሃብ",
    btn_mark_completed:   "ከም ዝተወድአ ምልክት ግበር",
    receipt:              "ቅብሊት",
    just_now:             "ሕጂ",

    table:                "ጠረጴዛ",
    round:                "ዙር",
    order:                "ትእዛዝ",
    items:                "ኣቕሑ",
    item:                 "ኣቕሓ",
    total:                "ድምር",
    notes:                "መዘኻኸሪ",
    note_for_kitchen:     "ንኽሽነ መዘኻኸሪ",
    waiter:               "ኣሳላፊ",
    chef:                 "ሼፍ",
    manager:              "ኣካያዲ ስራሕ",
    customer:             "ዓሚል",
    guest:                "ጋሻ",

    take_order_title:     "ኣብ ጠረጴዛ ትእዛዝ ውሰድ",
    select_table:         "ጠረጴዛ ምረጽ",
    search_items:         "ኣብ ሜኑ ድለ",
    cart:                 "ዘንቢል",
    cart_empty:           "ዘንቢል ጥራዩ እዩ — ንምውሳኽ ኣብ ላዕሊ ዘሎ ኣቕሓ ጠውቕ።",
    add_item_to_order:    "ናብ ትእዛዝ ወስኽ",
    review_and_submit:    "ርአ እሞ ለኣኽ",

    kitchen_queue_title:  "መስርዕ ክሽነ",
    no_orders_yet:        "ክሳብ ሕጂ ትእዛዝ የለን።",
    time_ago_minutes:     "ደቒቕ ይገብር",
    time_ago_just_now:    "ሕጂ",

    tabs_title:           "ክፉት ትእዛዛት",
    no_open_tabs:         "ሕጂ ክፉት ትእዛዝ የለን።",
    new_round:            "ሓድሽ ዙር",
    running_total:        "ዝቕጽል ድምር",
  },

  // ===================================================================
  // SIDAAMU AFOO (sid) — MACHINE DRAFT 2026-06-05, LOW CONFIDENCE.
  // Sidaamu Afoo is a low-resource language; these drafts lean on
  // loanwords (Meenyu, QR, Xaraphpheeza) and common Sidaama patterns.
  // EVERY value must be verified by a native speaker before the
  // Hawassa pilot — treat this purely as a starting point that is
  // better than an English-only screen for the expert reviewer.
  // ===================================================================
  sid: {
    nav_order_queue:      "Hajajote Garinni",
    nav_take_order:       "Tajaajilaanchunni Hajajo Adhi",
    nav_tabs:             "Fa'naqino Hajajuwa",
    nav_reports:          "Rippoorte",
    nav_menu:             "Meenyu Uduunne",
    nav_recommended:      "Doorraminoha",
    nav_tables:           "Xaraphpheezuwanna Kifiluwa",
    nav_qr_codes:         "QR Koodduwa",
    nav_staff:            "Loosaasinete Akkawunte",
    nav_assignments:      "Tajaajilaanchu Ramado",
    nav_ratings:          "Wo'naalshanna Hedo",
    nav_admin:            "Gashshaancho",
    nav_logout:           "Fuli",
    my_tables_today:      "Techo xaraphpheezuwakki",
    no_tables_assigned:   "Techo ramadhino xaraphpheeza dino",
    ask_manager:          "Gashshaanchu xaraphpheeza ramadannohe xa'mi.",
    nav_group_orders:     "Tajaajilote Gashshoote",
    nav_group_menu:       "Meenyu Gashshoote",
    nav_group_tableqr:    "Xaraphpheezanna QR Qixxeesso",
    nav_group_team:       "Gaamote Gashshoote",
    nav_group_insights:   "Gashshaanchu Huwato",

    status_pending:       "Agarate no",
    status_preparing:     "Qixxaawanni no",
    status_ready:         "Qixxaawino",
    status_served:        "Shiqqino",
    status_cancelled:     "Hunamino",
    status_open:          "Fa'naqino",
    status_closed:        "Cufamino",
    tab_active:           "Loosu aana",
    tab_all_today:        "Techo baala",
    refresh:              "Haaroonsi",
    loading:              "Dirriqamanni no…",
    no_orders_here:       "Konni hajajo dino.",
    signed_in_as:         "E'inohu",
    live:                 "suwashshu",
    polling:              "buuxxanni",

    btn_add:              "Ledi",
    btn_remove:           "Hooli",
    btn_submit:           "Soyi",
    btn_save:             "Suuqi",
    btn_cancel:           "Agura",
    btn_confirm:          "Buuxi",
    btn_edit:             "Biddeessi",
    btn_delete:           "Huni",
    btn_back:             "Higgi",
    btn_mark_preparing:   "Qixxeesso hanafi",
    btn_mark_ready:       "Qixxaawino yite malaatisi",
    btn_mark_served:      "Shiqqino yite malaatisi",
    btn_close_tab:        "Fa'naqino hajajo cufi",
    btn_submit_order:     "Hajajo soyi",
    btn_place_order:      "Hajajo uyi",
    btn_mark_completed:   "Gumulamino yite malaatisi",
    receipt:              "Nagaashshe",
    just_now:             "xa",

    table:                "Xaraphpheeza",
    round:                "Marro",
    order:                "Hajajo",
    items:                "Uduunne",
    item:                 "Uduunnicho",
    total:                "Xaphooma",
    notes:                "Qaagiishsha",
    note_for_kitchen:     "Kishinete qaagiishsha",
    waiter:               "Tajaajilaancho",
    chef:                 "Sheefe",
    manager:              "Gashshaancho",
    customer:             "Hidhaancho",
    guest:                "Wolqawaancho",

    take_order_title:     "Xaraphpheezate aana hajajo adhi",
    select_table:         "Xaraphpheeza doori",
    search_items:         "Meenyu giddo hasi",
    cart:                 "Diqqe",
    cart_empty:           "Diqqe mulla te — ledate aliidi uduunnicho xiiwi.",
    add_item_to_order:    "Hajajote ledi",
    review_and_submit:    "La'e soyi",

    kitchen_queue_title:  "Kishinete saffe",
    no_orders_yet:        "Geeshsha hajajo dino.",
    time_ago_minutes:     "daqiiqa albaanni",
    time_ago_just_now:    "xa",

    tabs_title:           "Fa'naqino hajajuwa",
    no_open_tabs:         "Xa fa'naqino hajajo dino.",
    new_round:            "Haaro marro",
    running_total:        "Massitanni Xaphooma",
  },

  // ===================================================================
  // NEDERLANDS (nl) — drafted 2026-06-05. High confidence; verify
  // with Dutch restaurant staff when Eindhoven onboards.
  // ===================================================================
  nl: {
    nav_order_queue:      "Bestelstatus",
    nav_take_order:       "Bestelling via ober",
    nav_tabs:             "Open bestellingen",
    nav_reports:          "Rapporten",
    dept_reports:         "Rapporten",               // TODO verify
    rpt_grp_close:        "Financiële afsluiting",   // TODO verify
    rpt_grp_analytics:    "Bedrijfsanalyse",         // TODO verify
    nav_fnb_analytics:    "F&B-analyse",             // TODO verify
    nav_business_overview: "Bedrijfsoverzicht",       // TODO verify
    nav_menu:             "Menu-items",
    nav_recommended:      "Aanbevelingen",
    nav_tables:           "Tafels & kamers",
    nav_qr_codes:         "QR-codes",
    nav_staff:            "Personeelsaccounts",
    nav_assignments:      "Obertoewijzing",
    nav_ratings:          "Beoordelingen & feedback",
    nav_admin:            "Beheer",
    nav_logout:           "Uitloggen",
    my_tables_today:      "Jouw tafels vandaag",
    no_tables_assigned:   "Vandaag geen tafels toegewezen",
    ask_manager:          "Vraag de manager om je tafels toe te wijzen.",
    nav_group_orders:     "Servicebeheer",
    nav_group_menu:       "Menubeheer",
    nav_group_tableqr:    "Tafels & QR-instellingen",
    nav_group_team:       "Teambeheer",
    nav_group_insights:   "Managerinzichten",

    status_pending:       "In afwachting",
    status_preparing:     "In bereiding",
    status_ready:         "Klaar",
    status_served:        "Geserveerd",
    status_cancelled:     "Geannuleerd",
    status_open:          "Open",
    status_closed:        "Gesloten",
    tab_active:           "Actief",
    tab_all_today:        "Alles vandaag",
    refresh:              "Vernieuwen",
    loading:              "Laden…",
    no_orders_here:       "Geen bestellingen hier.",
    signed_in_as:         "Ingelogd als",
    live:                 "live",
    polling:              "polling",

    btn_add:              "Toevoegen",
    btn_remove:           "Verwijderen",
    btn_submit:           "Versturen",
    btn_save:             "Opslaan",
    btn_cancel:           "Annuleren",
    btn_confirm:          "Bevestigen",
    btn_edit:             "Bewerken",
    btn_delete:           "Verwijderen",
    btn_back:             "Terug",
    btn_mark_preparing:   "Start bereiding",
    btn_mark_ready:       "Markeer als klaar",
    btn_mark_served:      "Markeer als geserveerd",
    btn_close_tab:        "Bestelling sluiten",
    btn_submit_order:     "Bestelling versturen",
    btn_place_order:      "Bestelling plaatsen",
    btn_mark_completed:   "Markeer als afgerond",
    receipt:              "Bon",
    just_now:             "zojuist",

    table:                "Tafel",
    round:                "Ronde",
    order:                "Bestelling",
    items:                "Items",
    item:                 "Item",
    total:                "Totaal",
    notes:                "Opmerkingen",
    note_for_kitchen:     "Opmerking voor de keuken",
    waiter:               "Ober",
    chef:                 "Chef",
    manager:              "Manager",
    customer:             "Klant",
    guest:                "Gast",

    take_order_title:     "Bestelling opnemen aan tafel",
    select_table:         "Selecteer tafel",
    search_items:         "Zoek menu-items",
    cart:                 "Winkelmand",
    cart_empty:           "De mand is leeg — tik op een item hierboven om toe te voegen.",
    add_item_to_order:    "Toevoegen aan bestelling",
    review_and_submit:    "Controleren &amp; versturen",

    kitchen_queue_title:  "Keukenwachtrij",
    no_orders_yet:        "Nog geen bestellingen.",
    time_ago_minutes:     "min geleden",
    time_ago_just_now:    "zojuist",

    tabs_title:           "Open bestellingen",
    no_open_tabs:         "Momenteel geen open bestellingen.",
    new_round:            "Nieuwe ronde",
    running_total:        "Lopend totaal",
  },
};

/** Resolve a key in the requested language, fall back to English, then
 *  return the key itself so a missing key is visible to developers. */
export function tFor(lang, key) {
  const bag = STRINGS[lang] || {};
  if (bag[key] !== undefined && bag[key] !== "") return bag[key];
  const en = STRINGS.en || {};
  if (en[key] !== undefined) return en[key];
  return key;
}
